import { EditorialAssetSearchResultItem, RelatedAsset } from 'utility/types/dataProvider';

/**
 * Relationship type for asset relationships
 */
export type RelationshipType = 'children' | 'parents' | 'siblings';

type CleanupRelationshipArgs = {
  targetRelationship: RelationshipType;
  assetsToAdd: EditorialAssetSearchResultItem[] | RelatedAsset[];
  currentChildren: RelatedAsset[];
  currentParents: RelatedAsset[];
  currentSiblings: RelatedAsset[];
};

type CleanupRelationshipResult = {
  cleanedChildren: RelatedAsset[];
  cleanedParents: RelatedAsset[];
  cleanedSiblings: RelatedAsset[];
};

/**
 * Removes assets from other relationship arrays when they are being added to a new relationship.
 * This ensures that an asset exists in only one relationship type at a time.
 *
 * @param targetRelationship - The relationship to add assets to
 * @param assetsToAdd - The assets being added
 * @param currentChildren - Current children array
 * @param currentParents - Current parents array
 * @param currentSiblings - Current siblings array
 * @returns Object with cleaned relationship arrays
 */
export function cleanupRelationships({
  targetRelationship,
  assetsToAdd,
  currentChildren = [],
  currentParents = [],
  currentSiblings = [],
}: CleanupRelationshipArgs): CleanupRelationshipResult {
  // Extract UUIDs from assets being added
  const addedUuids = new Set(assetsToAdd.map((asset) => asset.uuid).filter(Boolean));

  // Helper to filter out assets that are being added
  const filterOutAddedAssets = (assets: RelatedAsset[]) => {
    return assets.filter((asset) => !addedUuids.has(asset.uuid));
  };

  // Clean up the other relationships (not the target)
  const cleanedChildren =
    targetRelationship === 'children'
      ? currentChildren
      : filterOutAddedAssets(currentChildren);
  const cleanedParents =
    targetRelationship === 'parents'
      ? currentParents
      : filterOutAddedAssets(currentParents);
  const cleanedSiblings =
    targetRelationship === 'siblings'
      ? currentSiblings
      : filterOutAddedAssets(currentSiblings);

  return {
    cleanedChildren,
    cleanedParents,
    cleanedSiblings,
  };
}

/**
 * Transforms search result items to RelatedAsset format
 */
export function transformToRelatedAsset(
  item: Partial<EditorialAssetSearchResultItem>,
): RelatedAsset {
  return {
    uuid: item?.uuid || item?.id || '', // Fallback to id if uuid is undefined
    app: item?.app || 'editorial',
    title: item.title,
    type: item.type,
    start_time: item?.start_time,
    status: item?.status,
    draft: item?.draft,
    no_publish: item?.no_publish,
    authors: item?.authors,
    byline: item?.byline,
    summary: item?.summary,
    previews: item.previews,
  };
}

/**
 * Merges new assets into existing relationship array, avoiding duplicates
 */
export function mergeRelationshipAssets(
  existingAssets: RelatedAsset[] = [],
  newAssets: RelatedAsset[] = [],
): RelatedAsset[] {
  const existingUuids = new Set(existingAssets.map((asset) => asset.uuid));
  const merged = [
    ...existingAssets,
    ...newAssets.filter((asset) => !existingUuids.has(asset.uuid)),
  ];
  return merged;
}
